import { createStyles, makeStyles } from "@material-ui/core";
import {
    baseColors,
    Breakpoint,
    ButtonGroupSplit,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    HelpIcon,
    JobType,
    mediator,
    MessageComposer,
    RelodeTheme,
    Text,
    useDeviceSize,
    useRelodeAppUI,
} from "@relode/core";
import classnames from "classnames";
import { JobManagerContact } from "core/src/domain";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import * as React from "react";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            cardButton: {
                padding: 24,
            },
            sectionButton: {
                paddingTop: theme.spacing(3),
            },
            cardTitle: {
                fontSize: 24,
                lineHeight: "36px",
                padding: theme.spacing(3, 3, 1, 3),
            },
            dialogText: {
                fontSize: 24,
                lineHeight: "32px",
            },
            sectionText: {
                fontSize: 20,
                lineHeight: "32px",
                fontWeight: "bold",
            },
            helpSection: {
                width: "100%",
                backgroundColor: baseColors.warm_gray,
            },

            header: {
                padding: theme.spacing(6, 1),
                backgroundColor: baseColors.warm_gray,
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: theme.spacing(3),
                alignItems: "center",
            },
            mobileHeader: {
                padding: theme.spacing(2, 1),
                backgroundColor: baseColors.warm_gray,
                alignItems: "center",
            },
        }),
    { name: "MarketResearchHelp" }
);

const formatTelUri = (phone: string) => {
    if (!phone) return "";
    const phoneNumber = parsePhoneNumberFromString("+" + phone);
    return phoneNumber.getURI();
};

const formatPhoneText = (phone: string) => {
    if (!phone) return "";
    const phoneNumber = parsePhoneNumberFromString("+" + phone);
    return phoneNumber.formatNational();
};

interface MarketResearchHelpProps {
    onClose?: () => void;
    fullWidth?: boolean;
    jobName?: string;
    locationName?: string;
    jobManager?: JobManagerContact;
    annualSalary?: number;
}

const MarketResearchMarketResearchHelpOptionButtons = (
    props: MarketResearchHelpProps
) => {
    const {
        onClose,
        fullWidth,
        jobName,
        locationName,
        jobManager,
        annualSalary,
    } = props;
    const [messageComposerOpen, setMessageComposerOpen] = React.useState(false);
    const { ui: appUIState } = useRelodeAppUI();

    const handleItemClick = (option: { id: string; label: string }) => {
        const hourlySalary = !!annualSalary
            ? Math.round(annualSalary / 2080)
            : 0;
        switch (option.id) {
            case "open-full-time": {
                mediator.send("showAddJob", {
                    isEditJob: false,
                    parentPageTitle: appUIState.pageTitle,
                    seedData: {
                        jobName: jobName,
                        jobType: JobType.FullTime,
                        salary: annualSalary,
                    },
                });
                onClose?.();
                break;
            }

            case "open-contract": {
                mediator.send("showAddJob", {
                    isEditJob: false,
                    parentPageTitle: appUIState.pageTitle,
                    seedData: {
                        jobName: jobName,
                        jobType: JobType.Contract,
                        salary: hourlySalary,
                    },
                });
                onClose?.();
                break;
            }

            case "message-ae": {
                setMessageComposerOpen(true);
                break;
            }

            case "call-ae": {
                document.location.href = formatTelUri(jobManager?.phoneNumber);
                onClose?.();
                break;
            }
        }
    };

    const renderOption = (opt: { id: string; label: string }) => {
        if (opt.id === "call-ae") {
            return (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>call {jobManager?.fullName}</span>
                    <span>{formatPhoneText(jobManager?.phoneNumber)}</span>
                </div>
            );
        }
        return <span style={{ minWidth: 200 }}>{opt.label}</span>;
    };

    const getInitialMessageBodyText = () =>
        `I would like your assistance reviewing job market research for the following position:

Job: ${jobName}
Location: ${locationName ?? "Remote/National"}`;

    return (
        <>
            <ButtonGroupSplit
                onSelect={handleItemClick}
                renderOption={renderOption}
                label="let's get started"
                size="large"
                fullWidth={fullWidth}
                options={[
                    {
                        id: "open-full-time",
                        label: "hire full-time employee",
                    },
                    {
                        id: "open-contract",
                        label: "hire contract employee",
                    },
                    {
                        id: "message-ae",
                        label: `message ${jobManager?.fullName ?? "Sales"}`,
                    },
                    {
                        id: "call-ae",
                        label: `call AE (${
                            jobManager?.phoneNumber ?? "8447735633"
                        })`,
                    },
                ]}
            />
            {messageComposerOpen ? (
                <MessageComposer
                    jobName={`${jobName} in ${
                        locationName ?? "Remote/National"
                    }`}
                    open={messageComposerOpen}
                    initialRecipients={[
                        {
                            displayName: jobManager?.fullName,
                            roleName: jobManager?.roleName,
                            userId: jobManager?.userId,
                        },
                    ]}
                    initialBodyText={getInitialMessageBodyText()}
                    onClose={() => {
                        setMessageComposerOpen(false);
                        onClose?.();
                    }}
                />
            ) : null}
        </>
    );
};

export const MarketResearchHelpSection = (props: MarketResearchHelpProps) => {
    const classes = useStyles({});
    const device = useDeviceSize();
    return (
        <div
            className={classnames({
                [classes.mobileHeader]: device === "mobile",
                [classes.header]: device === "desktop",
            })}
        >
            <Breakpoint desktop>
                <div style={{ marginLeft: 32, display: "inline-flex" }}>
                    <HelpIcon fontSize="large" />
                </div>
            </Breakpoint>
            <Text variant="h2">How can we help?</Text>
            <div style={{ gridColumnStart: "2" }}>
                <Text variant="body2" className={classes.sectionText}>
                    Get help interpreting market research, hiring full-time or
                    contract employees, and more.
                </Text>
                <div className={classes.sectionButton}>
                    <MarketResearchMarketResearchHelpOptionButtons {...props} />
                </div>
            </div>
        </div>
    );
};

interface MarketResearchHelpDialogProps extends MarketResearchHelpProps {
    open: boolean;
}

export const MarketResearchHelpDialog = (
    props: MarketResearchHelpDialogProps
) => {
    const { open, ...passthrough } = props;
    const classes = useStyles({});

    return (
        <Dialog open={open} onClose={props.onClose}>
            <>
                <DialogTitle title="how can we help?" onClose={props.onClose} />
                <DialogContent>
                    <Text variant="body2" className={classes.dialogText}>
                        We are here to help you make sense of your job market
                        research data and turn it into an actionable hiring
                        plan.
                    </Text>
                    <br />
                    <Text variant="body2" className={classes.dialogText}>
                        Get help interpreting market research, hiring full-time
                        or contract employees, and more.
                    </Text>
                </DialogContent>
                <DialogActions>
                    <MarketResearchMarketResearchHelpOptionButtons
                        {...passthrough}
                    />
                </DialogActions>
            </>
        </Dialog>
    );
};
